//src/app/services/projects.tsx
import { ProgressStatus } from 'Components/ProgressBar';
import api, {
  buildQueryParams,
  IGetQueryParams,
  IPaginatedRepsonse,
} from './index';
import {
  IAccountantRecommendation,
  IProject,
  ProjectsResponse,
  ProjectStatusType,
} from './responseTypes';
import { IUser } from './user';

export interface IGetaxReturnStatsResponse {
  monday_statuses_count: { status: ProgressStatus; count: number }[];
  in_app_statuses_count: { status: ProjectStatusType; count: number }[];
  tax_returns_stats: {
    businesses_tax_returns_count: number;
    individual_tax_returns_count: number;
    total_tax_returns_count: number;
  };
}

export const projectsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getTaxReturnStats: builder.query<
      IGetaxReturnStatsResponse,
      IGetQueryParams<{
        created_at?: number;
      }>
    >({
      query: (params) => `/projects/stats${buildQueryParams(params)}`,
    }),

    getProjects: builder.query<
      IPaginatedRepsonse<IProject>,
      IGetQueryParams<{
        status?: ProjectStatusType[];
        search?: string;
        client_id?: number;
        accountant_id?: number;
        business_id?: number;
        year?: number;
      }>
    >({
      query: (params) => `/projects/paginated_index${buildQueryParams(params)}`,
    }),

    getAccountantRecommendations: builder.query<
      IAccountantRecommendation[],
      {
        projectId: number;
        enableTimezone: boolean;
        enableZipcode: boolean;
        enableSpecialization: boolean;
        enableLanguage: boolean;
        limit: number;
      }
    >({
      query: ({
        enableLanguage,
        enableSpecialization,
        enableZipcode,
        enableTimezone,
        projectId,
        limit,
      }) =>
        `/projects/${projectId}/recommend?enable_time_zone=${enableTimezone}&enable_zipcode=${enableZipcode}&enable_specialization=${enableSpecialization}&enable_language=${enableLanguage}&limit=${limit}`,
      providesTags: () => [{ type: 'Accountants', id: 'Recommendations' }],
      transformResponse: (response: {
        accountants: IAccountantRecommendation[];
      }) => response.accountants,
    }),
    getProjectById: builder.query<{ project: IProject }, string>({
      query: (id: string) => `/projects/${id}`,
      providesTags: () => [{ type: 'Project' }],
      transformResponse: ({
        project,
      }: {
        project: IProject;
      }): { project: IProject } => {
        return {
          project: {
            ...project,
            questionnaire: {
              ...project.questionnaire,
              data: {
                taxQuestionnaire: project.questionnaire.data.taxQuestionnaire,
                prepareQuestionnaire: {
                  // @ts-ignore
                  ...project.questionnaire.data.configureLifeIncome,
                  ...project.questionnaire.data.prepareQuestionnaire,
                },
              },
            },
          },
        };
      },
    }),

    copyProject: builder.mutation<{ project: IProject }, number>({
      query: (id) => ({
        url: `/projects/${id}/copy`,
        method: 'POST',
      }),
      invalidatesTags: () => [{ type: 'Project' }],

      onQueryStarted: (id, { dispatch, queryFulfilled }) => {
        return queryFulfilled.then((response: any) => {
          dispatch(
            projectsApi.util.updateQueryData(
              'getProjectById',
              id.toString(),
              (draft) => Object.assign(draft, response.data),
            ),
          );
        });
      },
    }),

    getProjectByFilterId: builder.query<
      IProject[],
      { client_id?: number; accountant_id?: number }
    >({
      query: (params) => `/projects${buildQueryParams({ filters: params })}`,
      providesTags: () => [{ type: 'Project' }],
      transformResponse: (response: ProjectsResponse) => {
        return response.projects.map((project) => ({
          ...project,
          year: project.questionnaire.year,
        }));
      },
    }),

    updateProject: builder.mutation({
      query: ({
        id,
        project,
      }: {
        id: string;
        project: {
          status_action: string;
          rejection_reasons?: [
            {
              reason: string;
            },
          ];
          assignees_ids?: number[];
        };
      }) => ({
        url: `/projects/${id}`,
        method: 'PUT',
        body: { project },
      }),
      invalidatesTags: [
        { type: 'Projects', id: 'LIST' },
        { type: 'Project' },
        { type: 'Accountants', id: 'Recommendations' },
      ],
    }),
    createProject: builder.mutation({
      // apply type for project insted of any
      query: (project: any) => ({
        url: `/projects`,
        method: 'POST',
        body: { project },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: [{ type: 'Projects', id: 'LIST' }, { type: 'Project' }],
    }),
    deleteProject: builder.mutation({
      query: (id: number) => ({
        url: `/projects/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Projects', id: 'LIST' }],
    }),
    setProjectPrice: builder.mutation({
      query: ({ id, price }: { id: number; price: number }) => ({
        url: `/projects/${id}/add_price`,
        method: 'PUT',
        body: { project: { price: price } },
      }),
      invalidatesTags: [{ type: 'Projects', id: 'LIST' }, { type: 'Project' }],
    }),

    payProject: builder.mutation<{ checkout_url: string }, number>({
      query: (id) => ({
        url: `/projects/${id}/pay`,
        method: 'POST',
      }),
      transformResponse: (response: { checkout_url: string }) => response,
    }),
    getIndividualTeamMembers: builder.query<IUser[], number>({
      query: (client_id) => `/teams?client_id=${client_id}`,
      providesTags: () => [{ type: 'Project' }],
      transformResponse: (response: { team_members: IUser[] }) =>
        response.team_members || [],
    }),
    getBusinessTeamMembers: builder.query<IUser[], number>({
      query: (business_id) => `/business_teams?business_id=${business_id}`,
      providesTags: () => [{ type: 'Project' }],
      transformResponse: (response: { team_members: IUser[] }) =>
        response.team_members || [],
    }),
  }),
});

export const {
  useLazyGetAccountantRecommendationsQuery,
  useLazyGetProjectsQuery,
  useGetProjectsQuery,
  useGetProjectByIdQuery,
  useCopyProjectMutation,
  useGetProjectByFilterIdQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useLazyGetIndividualTeamMembersQuery,
  useLazyGetBusinessTeamMembersQuery,
  useSetProjectPriceMutation,
  usePayProjectMutation,
  useGetTaxReturnStatsQuery,
} = projectsApi;

export const useGetProjectByIdQueryState =
  projectsApi.endpoints.getProjectById.useQueryState;
